import { Avatar } from "@dropbox/dig-components/avatar";
import { Table } from "@dropbox/dig-components/table";
import { Text } from "@dropbox/dig-components/typography";
import { Box } from "@dropbox/dig-foundations";
import { PAP_Open_DashLink } from "@mirage/analytics/events/types/open_dash_link";
import { createUxaElementId } from "@mirage/analytics/uxa";
import { FavIcon } from "@mirage/link-list/Favicon/Favicon";
import type { Link as LinkType } from "@mirage/link-list/types";
import { ListItemSize } from "@mirage/link-list/types";
import { Link } from "@mirage/link/Link";
import { useTheme } from "@mirage/service-settings/theming/useTheme";
import type { StackItemShortcutWithMetadata } from "@mirage/service-stacks/service/types";
import { stackItemGetName } from "@mirage/service-stacks/service/utils";
import { getInitials } from "@mirage/shared/account";
import { faviconSrcForSrcUrl } from "@mirage/shared/util/favicon";
import { getTimeAgoString } from "@mirage/shared/util/time";
import { nonNil } from "@mirage/shared/util/tiny-utils";
import { activeStackAtom } from "@mirage/stacks/atoms";
import { featureLine } from "@mirage/stacks/constants";
import { useSortedStacks } from "@mirage/stacks/hooks";
import { useReportStackPapEvent } from "@mirage/stacks/hooks/useReportStackPapEvent";
import i18n from "@mirage/translations";
import { useAtomValue } from "jotai";
import styles from "./SectionItemsTable.module.css";

export const SectionItemsTable = ({
  items,
}: {
  items: StackItemShortcutWithMetadata[];
}) => {
  const stack = useAtomValue(activeStackAtom);
  const allStacks = useSortedStacks();
  const isDarkMode = useTheme();
  const reportStackPapEvent = useReportStackPapEvent(stack);

  return (
    <div className={styles.container}>
      <Table spacing="large">
        <Table.Header>
          <Table.Row isHeader className={styles.row}>
            <Table.HeaderCell className={styles.columnLabel} width="50%">
              {i18n.t("stack_items_table_column_name")}
            </Table.HeaderCell>
            <Table.HeaderCell className={styles.columnLabel} width="39%">
              {i18n.t("stack_items_table_column_added_by")}
            </Table.HeaderCell>
            <Table.HeaderCell
              align="right"
              className={styles.columnLabel}
              width="11%"
            >
              {i18n.t("stack_items_table_column_modified")}
            </Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {items.map((item) => {
            const stackItemId = item.api_file_id;
            const link: LinkType = {
              id: stackItemId,
              title: stackItemGetName(item, allStacks),
              url: nonNil(item.url, "item.url"),
              sortKey: item.sort_key,
            };

            return (
              <Table.Row className={styles.row} key={stackItemId}>
                <Table.Cell className={styles.cell}>
                  <Box
                    display="flex"
                    flexDirection="row"
                    alignItems="center"
                    columnGap="Micro Small"
                  >
                    <FavIcon
                      src={faviconSrcForSrcUrl(link.url, 32, isDarkMode)}
                      size={ListItemSize.Small}
                    />
                    <Link
                      className={styles.stackItemLink}
                      href={link.url}
                      onOpen={() => reportStackPapEvent(PAP_Open_DashLink())}
                      data-uxa-log={createUxaElementId("stack_item", {
                        featureLine,
                      })}
                    >
                      {stackItemGetName(item, allStacks)}
                    </Link>
                  </Box>
                </Table.Cell>
                <Table.Cell>
                  <Box
                    display="flex"
                    flexDirection="row"
                    alignItems="center"
                    columnGap="Micro Small"
                  >
                    {item.creator?.display_name && (
                      <Avatar
                        src={item.creator.profile_photo_url || ""}
                        alt={item.creator.display_name}
                        size="medium"
                      >
                        {getInitials(item.creator.display_name)}
                      </Avatar>
                    )}
                    <Text color="subtle">{item.creator?.display_name}</Text>
                  </Box>
                </Table.Cell>
                <Table.Cell align="right">
                  <Text color="subtle">
                    {item.last_modified_time_utc_sec &&
                      getTimeAgoString(item.last_modified_time_utc_sec * 1000, {
                        capitalize: false,
                        abbreviateTime: true,
                        abbreviateDate: true,
                      })}
                  </Text>
                </Table.Cell>
              </Table.Row>
            );
          })}
        </Table.Body>
      </Table>
    </div>
  );
};
